import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import {
  injectTellentAssistantResourceIdGetter,
  injectTellentAssistantThreadsBaseUrl,
} from '../tellent-assistant-endpoint.provider';
import { TellentAssistantThreadsHttp } from '../tellent-assistant-threads.http';
import { TellentAssistantThreadsActions } from './tellent-assistant-threads.state';

export const loadTellentAssistantThreads = createEffect(
  (
    actions$ = inject(Actions),
    http = inject(TellentAssistantThreadsHttp),
    baseUrl = injectTellentAssistantThreadsBaseUrl({ optional: true }) ?? '',
    getResourceId = injectTellentAssistantResourceIdGetter({ optional: true }) ?? (() => null),
  ) =>
    actions$.pipe(
      ofType(TellentAssistantThreadsActions.fetchCollection),
      switchMap(() => {
        const resourceId = getResourceId();
        if (!resourceId || !baseUrl) {
          return of(
            TellentAssistantThreadsActions.fetchCollectionSuccess({ payload: { data: [] } }),
          );
        }
        return http.list(baseUrl, resourceId).pipe(
          map(data => TellentAssistantThreadsActions.fetchCollectionSuccess({ payload: { data } })),
          catchError((error: HttpErrorResponse) =>
            of(TellentAssistantThreadsActions.fetchCollectionFailed({ payload: { error } })),
          ),
        );
      }),
    ),
  { functional: true },
);

export const deleteTellentAssistantThread = createEffect(
  (
    actions$ = inject(Actions),
    http = inject(TellentAssistantThreadsHttp),
    baseUrl = injectTellentAssistantThreadsBaseUrl({ optional: true }) ?? '',
  ) =>
    actions$.pipe(
      ofType(TellentAssistantThreadsActions.removeOnePessimistic),
      switchMap(action => {
        const threadId = action.payload.data;
        return http.delete(baseUrl, threadId).pipe(
          map(() =>
            TellentAssistantThreadsActions.removeOnePessimisticSuccess({
              // Why: the reducer calls adapter.removeOne(state, data.id); only .id is read.
              // The action type requires a full ThreadSummary, but constructing one here
              // would be fabricated data — the server returns void (204). We cast to never
              // at this seam to keep the cast contained and explicit.
              payload: { data: <never>{ id: threadId } },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentAssistantThreadsActions.removeOnePessimisticFailed({
                payload: { data: threadId, error },
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);
